import type {
  DropdownMenuContentProps,
  DropdownMenuPortalProps,
} from '@radix-ui/react-dropdown-menu';
import {
  Arrow,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Portal,
  Root,
} from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';
import { ColorHex } from '../../constants';

export interface DropdownProps extends HTMLAttributes<HTMLDivElement> {
  /** Element that toggles the dropdown menu  */
  triggerElement: ReactNode;
  /** children of the dropdown menu */
  children: ReactNode;
  /** arrow that points to the trigger */
  arrow?: boolean;
  /** Optional open state to be used in controlled mode, should be used with onOpenChange  */
  open?: boolean;
  /** Event handler called when the open state of the dropdown menu changes. */
  onOpenChange?: (open: boolean) => void;
  /* makes trigger element the only child of root (helps avoid button nesting) */
  asChild?: boolean;
  /* disables the trigger element */
  disabled?: boolean;
  /* distance in pixels to render from Content's edge */
  arrowOffset?: number;
  /** allows interacting with elements outside of dropdown */
  interactive?: boolean;
  /** placement */
  side?: DropdownMenuContentProps['side'];
  /** The distance in pixels from the trigger. */
  sideOffset?: number;
  /** should the menu be scrollable */
  scroll?: boolean;
  /** The preferred alignment against the trigger  */
  align?: 'start' | 'center' | 'end';
  /** dropdown fixed width (default is auto) */
  width?: number | string;
  /** additional styles for the menu */
  contentClass?: string;
  /** Element to portal the content into. Will use `document.body` by default */
  container?: DropdownMenuPortalProps['container'];
  /** z-index for the dropdown content */
  zIndex?: number;
  /** invoked when user interacts outside the dropdown menu */
  onInteractOutside?: DropdownMenuContentProps['onInteractOutside'];
  /** Aria label for trigger button */
  triggerElementAriaLabel?: string;
}

export const Dropdown = ({
  triggerElement,
  triggerElementAriaLabel = 'dropdown-menu-trigger',
  children,
  arrow,
  open,
  onOpenChange,
  asChild,
  disabled,
  arrowOffset,
  interactive,
  side,
  sideOffset = 5,
  scroll,
  align = 'start',
  width,
  contentClass,
  container,
  zIndex,
  onInteractOutside,
  ...rest
}: DropdownProps) => {
  return (
    <Root
      open={!disabled && open}
      onOpenChange={onOpenChange}
      modal={!interactive}
    >
      <DropdownMenuTrigger
        asChild={asChild}
        disabled={disabled}
        aria-label={triggerElementAriaLabel}
      >
        {triggerElement}
      </DropdownMenuTrigger>
      <Portal container={container}>
        <DropdownMenuContent
          onInteractOutside={onInteractOutside}
          style={{ width, zIndex: zIndex ?? 9999 }}
          onCloseAutoFocus={(event) => event.preventDefault()}
          sideOffset={sideOffset}
          className={classNames(
            'bg-ds-accent3-100 rounded-md shadow-xl p-1',
            scroll ? 'overflow-y-auto max-h-80' : '',
            contentClass
          )}
          loop
          side={side}
          align={align}
          {...rest}
        >
          {children}
          {arrow && (
            <Arrow
              offset={arrowOffset}
              fill={ColorHex.accent3100}
            />
          )}
        </DropdownMenuContent>
      </Portal>
    </Root>
  );
};
